import { type ReactNode, useEffect } from "react";

import { cn } from "@/lib/utils";
import { resellerTitle } from "@/utils/reseller/applyResellerBranding";
import { useResellerBranding } from "@/utils/reseller/store";

import { ProofRotator } from "./ProofRotator";
import { ResellerLogo } from "./ResellerLogo";
import { useMediaQuery } from "./useMediaQuery";

/**
 * What a product supplies to get a login shell. Everything else — the split
 * layout, its breakpoints, the rotating copy, and the styling of every form
 * primitive — is the shared base (this file + base.css), so the products
 * cannot drift apart.
 */
export interface BrandShellConfig {
  /** Test id on the root, e.g. "gm-shell". */
  testId: string;
  /** Decorative panel background (aurora, scenes, grain). aria-hidden. */
  art: ReactNode;
  /** The product logo; `compact` is the <768px top-band layout. */
  logo: (compact: boolean) => ReactNode;
  /** Small caps line over the rotating copy. */
  eyebrow: string;
  /** Rotating panel sentences, ≤ 64 characters each; index 0 is the anchor. */
  proofs: readonly string[];
  /** Optional exit below the form, e.g. back to the marketing site. */
  home?: { href: string; label: string };
}

export interface BrandShellProps {
  config: BrandShellConfig;
  children: ReactNode;
  className?: string;
}

/**
 * Split-screen login shell shared by every branded product: a dark brand
 * panel on the left, the screen's own card/form (unchanged) on the right.
 * Below 768px the panel collapses into a top band. Rendered by
 * ULThemePageLayout when <html data-ul-brand="…"> is present.
 *
 * The optional `home` link ends the form column. A login screen is reached by
 * more than people who mean to sign in — an expired session lands here, and
 * so does a stale in-app link — and without an exit the only ways out are the
 * back button or retyping the domain.
 *
 * On a reseller's login (`useResellerBranding()`) the reseller's logo takes
 * the logo slot and the home link is dropped: it leads to the product's own
 * marketing site, which the reseller's customers should not be sent to. The
 * product's art, eyebrow and copy stay.
 */
export function BrandShell({ config, children, className }: BrandShellProps) {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const compact = useMediaQuery("(max-width: 767px)");
  const reseller = useResellerBranding();

  // Every screen assigns document.title while it renders, so the reseller's
  // title is put back after each render. No dependency list on purpose: the
  // screen re-renders this shell whenever it overwrites the title.
  useEffect(() => {
    if (reseller) document.title = resellerTitle(reseller);
  });

  return (
    <div className={cn("bs-shell", className)} data-testid={config.testId}>
      <aside className="bs-panel">
        {config.art}
        <div className="bs-panel__logo">
          {reseller ? (
            <ResellerLogo
              branding={reseller}
              compact={compact}
              fallback={config.logo(compact)}
            />
          ) : (
            config.logo(compact)
          )}
        </div>
        <ProofRotator
          proofs={config.proofs}
          eyebrow={config.eyebrow}
          isStatic={reducedMotion || compact}
        />
      </aside>
      <main className="bs-form">
        <div className="bs-form__inner">
          {children}
          {config.home && !reseller && (
            <a className="bs-form__home" href={config.home.href}>
              <span aria-hidden="true">&larr;</span> {config.home.label}
            </a>
          )}
        </div>
      </main>
    </div>
  );
}
